/**
 * views/Kitchen/KitchenTicketDrawer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * One ticket, opened out. The screen a dispute is settled on.
 *
 * ## Why a slide-over and not a Modal
 *
 * A react-bootstrap Modal is a light box centred over a light page. This is
 * read against the dark board, usually with the board still visible behind it,
 * and its contents are a long scroll: order context, five clocks, the items,
 * and an audit trail that can run to fifty rows. A right-hand panel keeps the
 * lane the ticket came from in view, scrolls independently, and does not fight
 * the board's palette. It is deliberately built from the KDS tokens rather
 * than the light admin theme for that reason.
 *
 * ## What it does NOT do
 *
 * It never writes. Every action that moves food lives on the board, where the
 * operator's hands already are. The only button here that touches the network
 * besides the reload is `Verify audit trail`, and that endpoint is read-only.
 * Keeping this surface read-only is what makes it safe to hand to a manager
 * arguing about a refund.
 *
 * @see controllers/kitchenController.js  getKitchenTicket, verifyKitchenTicket
 * @see services/kitchen/kitchenAudit.js  verifyTicketChain
 * @see models/KitchenTicketEventModel.js the append-only log this renders
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiRefreshCw,
  FiShield,
  FiX,
} from "react-icons/fi";

import { apiKitchenStations, apiKitchenTicket, apiKitchenVerifyTicket } from "../../api/index";
import {
  KDS,
  STATUS_META,
  SLA_META,
  ITEM_STATUS_META,
  KITCHEN_VOID_REASONS,
  describeOptions,
  formatClock,
  formatDuration,
  formatMinutes,
  formatMoney,
  stationColour,
} from "./kitchenUi";

/** Plain English for the audit vocabulary in constants/kitchen.js KITCHEN_EVENT. */
const EVENT_LABEL = {
  TICKET_CREATED: "Ticket created",
  STATUS_CHANGED: "Status changed",
  STATUS_RECALLED: "Status pulled back",
  ITEM_STATUS_CHANGED: "Item status changed",
  ITEM_VOIDED: "Item voided",
  ITEM_RESTORED: "Item put back",
  STATION_REROUTED: "Moved to another station",
  PRIORITY_CHANGED: "Priority changed",
  NOTE_ADDED: "Note added",
  SLA_BREACHED: "Prep time breached",
  RIDER_ASSIGNED: "Rider assigned",
  RIDER_ARRIVED: "Rider arrived",
  ORDER_PICKED_UP: "Picked up",
  ORDER_CANCELLED_UPSTREAM: "Cancelled upstream",
};

/**
 * Who did it, in the words a restaurant owner would use. `staff` is called out
 * in a different colour on purpose: "Lusuku touched my ticket" is the single
 * most common question this screen is opened to answer.
 */
const ACTOR_TYPE_META = {
  vendor: { label: "restaurant", colour: KDS.textMid },
  staff: { label: "Lusuku staff", colour: KDS.info },
  rider: { label: "rider", colour: KDS.accepted },
  system: { label: "automatic", colour: KDS.textDim },
};

const PANEL_WIDTH = 560;

const readError = (err, fallback) => {
  // 403 is the one status worth its own sentence: the board can legitimately
  // hold a stale id, and "belongs to another kitchen" is a different problem
  // from "broken", which is what a generic failure message would imply.
  if (err?.response?.status === 403) return "This ticket belongs to another kitchen.";
  return err?.response?.data?.message || fallback;
};

const isFiniteNumber = (value) => Number.isFinite(Number(value));

/**
 * A void reason typed by hand deserves more scrutiny than one picked off the
 * standard list, so the two are told apart rather than rendered identically.
 */
const isStandardVoidReason = (reason) => {
  const text = String(reason || "").trim().toLowerCase();
  if (!text) return false;
  return KITCHEN_VOID_REASONS.some((r) => r.toLowerCase() === text);
};

// ── Small pieces ─────────────────────────────────────────────────────────────
function Pill({ children, colour, filled = false, title }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        background: filled ? colour : "transparent",
        color: filled ? KDS.bg : colour,
        border: `1px solid ${colour}`,
      }}
    >
      {children}
    </span>
  );
}

function Section({ title, note, children }) {
  return (
    <section
      style={{
        borderTop: `1px solid ${KDS.border}`,
        padding: "18px 20px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "1px",
          textTransform: "uppercase",
          color: KDS.textDim,
        }}
      >
        {title}
      </div>
      {note ? (
        <div style={{ fontSize: 12.5, color: KDS.textMid, marginTop: 6, lineHeight: 1.5 }}>
          {note}
        </div>
      ) : null}
      <div style={{ marginTop: 12 }}>{children}</div>
    </section>
  );
}

function Field({ label, value, accent }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "5px 0", alignItems: "baseline" }}>
      <div style={{ flex: "0 0 132px", fontSize: 12, color: KDS.textDim }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13.5, color: accent || KDS.text, wordBreak: "break-word" }}>
        {value === null || value === undefined || value === "" ? (
          <span style={{ color: KDS.textDim }}>Not recorded</span>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function Chip({ children, colour }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11.5,
        background: KDS.bgLane,
        border: `1px solid ${colour || KDS.border}`,
        color: colour || KDS.textMid,
        marginRight: 6,
        marginTop: 4,
      }}
    >
      {children}
    </span>
  );
}

// ── One row of the audit trail ───────────────────────────────────────────────
/**
 * Oldest first, one row per event, with the gap since the previous row spelled
 * out. The gaps are the whole point: a trail that only shows wall-clock times
 * makes the reader do the subtraction, and the interesting number in a dispute
 * is almost always "how long was it sitting there", not "what time was it".
 */
function TimelineEvent({ event, isLast, expanded, onToggle, stations }) {
  const actorMeta = ACTOR_TYPE_META[event?.actor?.type] || ACTOR_TYPE_META.system;
  const label = EVENT_LABEL[event?.type] || event?.type || "Unknown event";
  const from = event?.fromStatus ? STATUS_META[event.fromStatus]?.label || event.fromStatus : null;
  const to = event?.toStatus ? STATUS_META[event.toStatus]?.label || event.toStatus : null;

  const payload = event?.payload;
  const hasPayload =
    payload && typeof payload === "object" ? Object.keys(payload).length > 0 : !!payload;

  let payloadText = "";
  if (hasPayload && expanded) {
    try {
      payloadText = JSON.stringify(payload, null, 2);
    } catch (err) {
      payloadText = String(payload);
    }
    // A payload carrying a whole basket can run to thousands of characters and
    // nobody reads past the first screen of it.
    if (payloadText.length > 4000) {
      payloadText = `${payloadText.slice(0, 4000)}\n\n[truncated for display]`;
    }
  }

  return (
    <div style={{ display: "flex", gap: 12 }}>
      <div style={{ flex: "0 0 14px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: actorMeta.colour,
            marginTop: 6,
            flex: "0 0 auto",
          }}
        />
        {!isLast ? <span style={{ flex: 1, width: 1, background: KDS.border, marginTop: 4 }} /> : null}
      </div>

      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 18 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: KDS.textDim, fontVariantNumeric: "tabular-nums" }}>
            #{event?.seq}
          </span>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: KDS.text }}>{label}</span>
          {from || to ? (
            <span style={{ fontSize: 12.5, color: KDS.textMid }}>
              {from || "start"} → {to || "unchanged"}
            </span>
          ) : null}
        </div>

        <div style={{ marginTop: 4, fontSize: 12.5, color: KDS.textMid }}>
          {formatClock(event?.at)}
          {isFiniteNumber(event?.elapsedFromPrevMs) && Number(event.elapsedFromPrevMs) > 0 ? (
            <span style={{ color: KDS.textDim }}>
              {" "}
              (+{formatDuration(event.elapsedFromPrevMs)} since the last event)
            </span>
          ) : (
            <span style={{ color: KDS.textDim }}> (first event on this ticket)</span>
          )}
        </div>

        {event?.itemName || event?.station ? (
          <div style={{ marginTop: 4 }}>
            {event?.itemName ? (
              <Chip>
                {event.itemQty ? `${event.itemQty} x ` : ""}
                {event.itemName}
              </Chip>
            ) : null}
            {event?.station ? (
              <Chip colour={stationColour(event.station, stations)}>{event.station}</Chip>
            ) : null}
          </div>
        ) : null}

        {event?.reason ? (
          <div style={{ marginTop: 6, fontSize: 12.5, color: KDS.text }}>
            <span style={{ color: KDS.textDim }}>Reason: </span>
            {event.reason}
            {event?.reasonCode ? (
              <span style={{ color: KDS.textDim }}> ({event.reasonCode})</span>
            ) : null}
          </div>
        ) : null}

        <div style={{ marginTop: 6, fontSize: 12, color: KDS.textDim }}>
          {event?.actor?.name || "Unnamed actor"}
          <span style={{ color: actorMeta.colour, fontWeight: 700 }}> {actorMeta.label}</span>
          {event?.actor?.source ? (
            <span> via {String(event.actor.source).toLowerCase()}</span>
          ) : null}
          {event?.actor?.role ? <span> ({event.actor.role})</span> : null}
        </div>

        {hasPayload ? (
          <div style={{ marginTop: 6 }}>
            <button
              type="button"
              onClick={onToggle}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                color: KDS.info,
                fontSize: 12,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {expanded ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
              details
            </button>
            {expanded ? (
              <pre
                style={{
                  marginTop: 6,
                  marginBottom: 0,
                  padding: 10,
                  borderRadius: KDS.radiusSm,
                  background: KDS.bg,
                  border: `1px solid ${KDS.border}`,
                  color: KDS.textMid,
                  fontSize: 11.5,
                  lineHeight: 1.45,
                  maxHeight: 260,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {payloadText}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Chain verification ───────────────────────────────────────────────────────
function VerifyPanel({ report, error, busy, onVerify }) {
  const intact = !!report?.intact;
  const issues = report?.issues || [];

  return (
    <div>
      <button
        type="button"
        onClick={onVerify}
        disabled={busy}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderRadius: KDS.radiusSm,
          border: `1px solid ${KDS.borderBright}`,
          background: KDS.bgTile,
          color: KDS.text,
          fontSize: 13,
          fontWeight: 600,
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.6 : 1,
        }}
      >
        <FiShield size={14} />
        {busy ? "Checking" : "Verify audit trail"}
      </button>

      {error ? (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: KDS.radiusSm,
            background: KDS.dangerSoft,
            border: `1px solid ${KDS.danger}`,
            color: KDS.danger,
            fontSize: 12.5,
          }}
        >
          {error}
        </div>
      ) : null}

      {report ? (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            borderRadius: KDS.radiusSm,
            background: intact ? "rgba(34,197,94,0.10)" : KDS.dangerSoft,
            border: `1px solid ${intact ? KDS.success : KDS.danger}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: intact ? KDS.success : KDS.danger,
              fontWeight: 700,
              fontSize: 13.5,
            }}
          >
            {intact ? <FiCheckCircle size={15} /> : <FiAlertTriangle size={15} />}
            {intact
              ? `Intact. ${report.eventCount} ${
                  report.eventCount === 1 ? "event" : "events"
                }, chain verified.`
              : `Chain broken. ${issues.length} ${issues.length === 1 ? "problem" : "problems"} found across ${
                  report.eventCount
                } ${report.eventCount === 1 ? "event" : "events"}.`}
          </div>

          {!intact ? (
            <ul style={{ margin: "10px 0 0 0", paddingLeft: 18, color: KDS.text, fontSize: 12.5 }}>
              {issues.map((issue, index) => (
                <li key={`${issue.seq}-${issue.kind}-${index}`} style={{ marginBottom: 6 }}>
                  <strong>Event #{issue.seq}</strong>{" "}
                  <span style={{ color: KDS.danger }}>{issue.kind}</span>
                  <div style={{ color: KDS.textMid }}>{issue.detail}</div>
                </li>
              ))}
            </ul>
          ) : null}

          <div style={{ marginTop: 10, fontSize: 11.5, color: KDS.textDim }}>
            Checked at {formatClock(report.verifiedAt)}.
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: 12, fontSize: 12, color: KDS.textDim, lineHeight: 1.55 }}>
        <strong style={{ color: KDS.textMid }}>What this proves.</strong> Every event is numbered
        in order and carries a hash of its own contents plus the hash of the event before it. The
        check recomputes both, so a row that was edited or deleted after the fact shows up here.
        <br />
        <strong style={{ color: KDS.textMid }}>What it does not prove.</strong> This is evidence,
        not proof. Anyone with unrestricted write access to the database could rewrite every row
        and recompute the whole chain. It catches silent partial edits, which is the realistic
        risk and the thing an auditor asks to see.
      </div>
    </div>
  );
}

// ── One cookable line ────────────────────────────────────────────────────────
function ItemRow({ item, stations }) {
  const meta = ITEM_STATUS_META[item?.status] || { label: item?.status || "Unknown", colour: KDS.textMid };
  const colour = stationColour(item?.station, stations);
  const options = describeOptions(item);
  const voided = item?.status === "VOIDED";

  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: KDS.radiusSm,
        background: KDS.bgTile,
        border: `1px solid ${KDS.border}`,
        marginBottom: 8,
        opacity: voided ? 0.75 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: KDS.text, fontVariantNumeric: "tabular-nums" }}>
          {item?.qty ?? 1}
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 14,
            color: KDS.text,
            textDecoration: voided ? "line-through" : "none",
          }}
        >
          {item?.name || "Unnamed item"}
        </span>
        <Pill colour={meta.colour}>{meta.label}</Pill>
      </div>

      <div style={{ marginTop: 2 }}>
        <Chip colour={colour}>{item?.stationName || item?.station || "MAIN"}</Chip>
        {options.map((option, index) => (
          <Chip key={`${option}-${index}`}>{option}</Chip>
        ))}
      </div>

      {item?.notes ? (
        <div style={{ marginTop: 6, fontSize: 12.5, color: KDS.textMid }}>
          <span style={{ color: KDS.textDim }}>Note: </span>
          {item.notes}
        </div>
      ) : null}

      {voided ? (
        <div style={{ marginTop: 6, fontSize: 12.5, color: KDS.danger }}>
          Voided: {item?.voidReason || "no reason recorded"}
          {item?.voidReason && !isStandardVoidReason(item.voidReason) ? (
            <span style={{ color: KDS.textDim }}> (typed by hand, not a standard reason)</span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── The drawer ───────────────────────────────────────────────────────────────
export function KitchenTicketDrawer({ ticketId, open, onClose, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [order, setOrder] = useState(null);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [verifyReport, setVerifyReport] = useState(null);
  const [verifyError, setVerifyError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    // A verification belongs to the ticket it was run against. Carrying it
    // across a change of ticket would put a green tick under someone else's
    // trail, which is the one thing this screen must never do.
    setVerifyReport(null);
    setVerifyError(null);
    setExpanded({});

    try {
      const { data } = await apiKitchenTicket(ticketId);
      const payload = data?.data || {};
      setTicket(payload.ticket || null);
      setEvents(Array.isArray(payload.events) ? payload.events : []);
      setOrder(payload.order || null);

      /* Station colours live in the vendor's station config, not on the ticket.
         Fetched separately, not awaited, and allowed to fail: without it
         `stationColour` falls back to a hash of the code, which is stable but
         would disagree with the colour that station wears on the board.
         Threading the ticket's OWN vendorId is what keeps this correct for
         Lusuku staff, whose token does not resolve to a single kitchen. */
      apiKitchenStations(
        payload.ticket?.vendorId ? { vendorId: String(payload.ticket.vendorId) } : {}
      )
        .then((res) => setStations(res?.data?.data?.stations || []))
        .catch(() => setStations([]));
    } catch (err) {
      setTicket(null);
      setEvents([]);
      setOrder(null);
      setError(readError(err, "Could not load that ticket."));
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (open && ticketId) load();
  }, [open, ticketId, load]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape" && typeof onClose === "function") onClose();
    };
    window.addEventListener("keydown", onKey);

    // The panel scrolls; the page behind it should not scroll with it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  const refresh = useCallback(async () => {
    await load();
    /* This drawer never writes, so `onChanged` is not a "something moved"
       signal. It fires after a manual reload to tell the board that its own
       cached projection of this ticket may be older than what is on screen
       here, which is the only way the two can drift while the drawer is open. */
    if (typeof onChanged === "function") onChanged();
  }, [load, onChanged]);

  const verify = useCallback(async () => {
    if (!ticketId) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const { data } = await apiKitchenVerifyTicket(ticketId);
      setVerifyReport(data?.data || null);
    } catch (err) {
      setVerifyReport(null);
      setVerifyError(readError(err, "Could not verify the audit trail."));
    } finally {
      setVerifying(false);
    }
  }, [ticketId]);

  // Oldest first. The server already sorts by seq, but the trail is read as a
  // story and a re-sort here costs nothing next to getting the order wrong.
  const orderedEvents = useMemo(
    () => [...events].sort((a, b) => (Number(a?.seq) || 0) - (Number(b?.seq) || 0)),
    [events]
  );

  if (!open) return null;

  const statusMeta = STATUS_META[ticket?.status] || {
    label: ticket?.status || "Unknown",
    colour: KDS.textMid,
  };
  const slaMeta = SLA_META[ticket?.sla?.state] || null;
  const durations = ticket?.durations || {};
  const fulfilment = ticket?.fulfilment || {};
  const items = ticket?.items || [];

  const clockRows = [
    ["Placed", ticket?.placedAt],
    ["Accepted", ticket?.acknowledgedAt],
    ["Cooking started", ticket?.prepStartedAt],
    ["Food up", ticket?.readyAt],
    ["Handed over", ticket?.handedOffAt],
  ];

  const durationRows = [
    ["Waiting to be accepted", durations.toAcknowledgeMs],
    ["Accepted to cooking", durations.toPrepStartMs],
    ["Cooking", durations.prepMs],
    ["Ready to handed over", durations.readyToHandoffMs],
    ["Total in the kitchen", durations.totalKitchenMs],
  ];

  const deliveryStatus = order?.delivery_status || ticket?.lastKnownDeliveryStatus || null;
  const mirrorDiffers =
    !!order?.delivery_status &&
    !!ticket?.lastKnownDeliveryStatus &&
    order.delivery_status !== ticket.lastKnownDeliveryStatus;

  return (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(3, 7, 18, 0.62)",
          zIndex: 1080,
        }}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Kitchen ticket detail"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100vh",
          width: `min(${PANEL_WIDTH}px, 100vw)`,
          background: KDS.bgPanel,
          borderLeft: `1px solid ${KDS.borderBright}`,
          boxShadow: KDS.shadow,
          color: KDS.text,
          overflowY: "auto",
          zIndex: 1090,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Sticky inside the scrolling panel: the ticket code and its status
            are the two things a reader needs while scrolling the trail. */}
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            background: KDS.bgPanel,
            borderBottom: `1px solid ${KDS.border}`,
            padding: "16px 20px 14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "0.4px" }}>
                {ticket?.ticketCode || (ticket?.ticketNumber ? `#${ticket.ticketNumber}` : "Ticket")}
              </div>
              <div style={{ fontSize: 12.5, color: KDS.textMid, marginTop: 2 }}>
                Order {ticket?.orderCode || "not recorded"}
                {ticket?.restaurantName ? ` · ${ticket.restaurantName}` : ""}
              </div>
            </div>

            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              aria-label="Reload this ticket"
              title="Reload this ticket"
              style={{
                background: "transparent",
                border: `1px solid ${KDS.border}`,
                borderRadius: KDS.radiusSm,
                color: KDS.textMid,
                padding: "6px 8px",
                cursor: loading ? "default" : "pointer",
              }}
            >
              <FiRefreshCw size={15} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close ticket detail"
              style={{
                background: "transparent",
                border: `1px solid ${KDS.border}`,
                borderRadius: KDS.radiusSm,
                color: KDS.text,
                padding: "6px 8px",
                cursor: "pointer",
              }}
            >
              <FiX size={16} />
            </button>
          </div>

          {ticket ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12, alignItems: "center" }}>
              <Pill colour={statusMeta.colour} filled>
                {statusMeta.label}
              </Pill>
              {slaMeta ? <Pill colour={slaMeta.colour}>{slaMeta.label}</Pill> : null}
              <span style={{ fontSize: 12.5, color: KDS.textMid }}>
                {formatDuration(ticket.ageMs)} old
              </span>
              <span style={{ fontSize: 12.5, color: KDS.textDim }}>
                {isFiniteNumber(ticket.promisedPrepMinutes)
                  ? `promised ${ticket.promisedPrepMinutes} min`
                  : "no promise recorded"}
              </span>
            </div>
          ) : null}
        </header>

        {loading ? (
          <div style={{ padding: "28px 20px", fontSize: 13.5, color: KDS.textMid }}>
            Loading the ticket.
          </div>
        ) : null}

        {error && !loading ? (
          <div style={{ padding: "24px 20px" }}>
            <div
              style={{
                padding: "14px 16px",
                borderRadius: KDS.radiusSm,
                background: KDS.dangerSoft,
                border: `1px solid ${KDS.danger}`,
                color: KDS.danger,
                fontSize: 13.5,
              }}
            >
              {error}
            </div>
            <button
              type="button"
              onClick={load}
              style={{
                marginTop: 12,
                background: KDS.bgTile,
                border: `1px solid ${KDS.borderBright}`,
                borderRadius: KDS.radiusSm,
                color: KDS.text,
                padding: "7px 14px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        ) : null}

        {ticket && !loading && !error ? (
          <div style={{ flex: 1 }}>
            <Section title="Order">
              <Field label="Customer" value={ticket.customerName || order?.customerName} />
              <Field
                label="Phone"
                value={ticket.customerPhone || order?.shippingAddress?.customerNumber}
              />
              <Field label="Delivery area" value={ticket.deliveryArea} />
              <Field
                label="Payment"
                value={
                  <span>
                    {ticket.paymentMethod || order?.paymentMethod || "Not recorded"}
                    <span style={{ marginLeft: 8 }}>
                      <Pill colour={ticket.isPaid || order?.isPaid ? KDS.success : KDS.atRisk}>
                        {ticket.isPaid || order?.isPaid ? "Paid" : "Not paid"}
                      </Pill>
                    </span>
                  </span>
                }
              />
              <Field
                label="Total"
                value={formatMoney(
                  ticket.totalPrice ?? order?.totalPrice,
                  ticket.currency
                )}
              />
              <Field label="Order source" value={ticket.orderSource} />
              {ticket.isMultiVendorOrder ? (
                <Field
                  label="Basket"
                  value="Part of a multi vendor order. Another kitchen holds its own ticket for the rest."
                />
              ) : null}

              {/* delivery_status is the ORDER's clock and is never written by
                  the kitchen (constants/kitchen.js). Shown in its own block so
                  it cannot be mistaken for the status pill in the header. */}
              <div
                style={{
                  marginTop: 14,
                  padding: "12px 14px",
                  borderRadius: KDS.radiusSm,
                  background: KDS.bgLane,
                  border: `1px solid ${KDS.border}`,
                }}
              >
                <div style={{ fontSize: 11, letterSpacing: "0.6px", textTransform: "uppercase", color: KDS.textDim }}>
                  Delivery status of the order
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, color: KDS.text }}>
                  {deliveryStatus || "Not recorded"}
                </div>
                <div style={{ fontSize: 12, color: KDS.textMid, marginTop: 6, lineHeight: 1.5 }}>
                  The kitchen status and the delivery status are two different clocks. The kitchen
                  one stops the moment the food leaves the pass. The delivery one keeps running
                  until the customer is holding the order.
                </div>
                {mirrorDiffers ? (
                  <div style={{ fontSize: 12, color: KDS.atRisk, marginTop: 6 }}>
                    The copy stored on this ticket still says {ticket.lastKnownDeliveryStatus}. That
                    mirror lags by design and is not what dispatch reads.
                  </div>
                ) : null}
              </div>
            </Section>

            <Section title="Timings">
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <tbody>
                  {clockRows.map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ padding: "4px 0", color: KDS.textDim, width: 170 }}>{label}</td>
                      <td style={{ padding: "4px 0", color: value ? KDS.text : KDS.textDim, fontVariantNumeric: "tabular-nums" }}>
                        {formatClock(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: `1px dashed ${KDS.border}`,
                }}
              >
                <div style={{ fontSize: 11.5, color: KDS.textDim, marginBottom: 6 }}>
                  Banked stage durations. Written once, when each stage ended, so a later recall or
                  a clock change cannot move a number that has already been measured.
                </div>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                  <tbody>
                    {durationRows.map(([label, value]) => (
                      <tr key={label}>
                        <td style={{ padding: "4px 0", color: KDS.textDim, width: 170 }}>{label}</td>
                        <td style={{ padding: "4px 0", color: isFiniteNumber(value) ? KDS.text : KDS.textDim, fontVariantNumeric: "tabular-nums" }}>
                          {formatDuration(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {isFiniteNumber(durations.totalKitchenMs) && isFiniteNumber(ticket.promisedPrepMinutes) ? (
                  <div style={{ marginTop: 10, fontSize: 12.5, color: KDS.textMid }}>
                    That is {formatMinutes(durations.totalKitchenMs)} against a promise of{" "}
                    {ticket.promisedPrepMinutes} min.
                  </div>
                ) : null}
              </div>
            </Section>

            <Section
              title="Handover"
              note="Two different waits, and they point at two different people."
            >
              <Field
                label="Rider waited"
                value={formatDuration(fulfilment.riderWaitMs)}
                accent={isFiniteNumber(fulfilment.riderWaitMs) ? KDS.atRisk : undefined}
              />
              <Field
                label="Food waited"
                value={formatDuration(fulfilment.foodWaitMs)}
                accent={isFiniteNumber(fulfilment.foodWaitMs) ? KDS.late : undefined}
              />
              <div style={{ fontSize: 12, color: KDS.textMid, marginTop: 8, lineHeight: 1.55 }}>
                Rider waited means the rider was standing at the pass while the food was still
                being made, so the delay sits with the kitchen. Food waited means the food was up
                and going cold with nobody there to take it, so the delay sits with dispatch.
                Normally only one of the two is set.
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Handover type" value={fulfilment.handoffType} />
                <Field label="Rider" value={fulfilment.riderName} />
                <Field label="Rider arrived" value={formatClock(fulfilment.riderArrivedAt)} />
                <Field label="Picked up" value={formatClock(fulfilment.pickedUpAt)} />
              </div>
            </Section>

            <Section title={`Items (${items.length})`}>
              {items.length === 0 ? (
                <div style={{ fontSize: 13, color: KDS.textDim }}>
                  This ticket carries no lines, which should not happen. Worth reporting.
                </div>
              ) : (
                items.map((item, index) => (
                  <ItemRow key={item.lineKey || `${item.name}-${index}`} item={item} stations={stations} />
                ))
              )}
            </Section>

            <Section
              title="Audit trail"
              note="Every event ever recorded against this ticket, oldest first. The log is append only, so a correction appears as a new row rather than an edit to an old one."
            >
              {orderedEvents.length === 0 ? (
                <div style={{ fontSize: 13, color: KDS.textDim }}>
                  No events recorded. A ticket always logs its own creation, so an empty trail is
                  itself a finding.
                </div>
              ) : (
                <div>
                  {orderedEvents.map((event, index) => (
                    <TimelineEvent
                      key={event._id || event.seq}
                      event={event}
                      isLast={index === orderedEvents.length - 1}
                      stations={stations}
                      expanded={!!expanded[event.seq]}
                      onToggle={() =>
                        setExpanded((prev) => ({ ...prev, [event.seq]: !prev[event.seq] }))
                      }
                    />
                  ))}
                </div>
              )}

              <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px dashed ${KDS.border}` }}>
                <VerifyPanel
                  report={verifyReport}
                  error={verifyError}
                  busy={verifying}
                  onVerify={verify}
                />
              </div>
            </Section>
          </div>
        ) : null}
      </aside>
    </>
  );
}

export default KitchenTicketDrawer;
